import { useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { getCurrentWebview } from "@tauri-apps/api/webview";
import { readTextFile } from "@tauri-apps/plugin-fs";
import { get, post } from "@/lib/api";
import { countWords } from "@/lib/editor";
import { isDesktopApp } from "@/lib/runtime";
import { toast } from "@/hooks/use-toast";
import type { Note } from "@/types";

/**
 * The window as an inbox (Mac app only).
 *
 * Drag a sentence out of Safari or Mail and drop it anywhere in the app: it
 * is filed as a loose note. Drop a `.md` or `.txt` file from the Finder: it
 * becomes a document. No dialog, no form — capture first, organise later,
 * which is how the rest of the Mac behaves.
 *
 * Two channels because that is how a WebView receives drops: text selections
 * arrive as DOM drag events; Finder files are intercepted by Tauri and arrive
 * as native paths. Drops aimed at a text field are left alone — dropping into
 * the editor should insert, not file.
 */

const TEXT_FILE = /\.(md|markdown|txt)$/i;

function targetHandlesDrop(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    target.closest("textarea, input, [contenteditable=true]") !== null
  );
}

function fileStem(path: string): string {
  const name = path.split("/").pop() ?? path;
  return name.replace(TEXT_FILE, "");
}

/** The imported document needs a type the server will accept; "Notes" is the
 * least presumptuous shelf for text of unknown intent. */
async function importDocTypeId(): Promise<number | null> {
  const types = await get<{ id: number; name: string }[]>("/document-types");
  if (!types.length) return null;
  const preferred = ["notes", "document", "sermon"];
  for (const want of preferred) {
    const hit = types.find((type) => type.name.toLowerCase() === want);
    if (hit) return hit.id;
  }
  return types[0].id;
}

export function DropCapture() {
  const [, navigate] = useLocation();
  const busyRef = useRef(false);

  // Text selections dropped from other apps (or other parts of this one).
  useEffect(() => {
    if (!isDesktopApp) return;

    const onDragOver = (event: DragEvent) => {
      if (targetHandlesDrop(event.target)) return;
      if (!event.dataTransfer?.types.includes("text/plain")) return;
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
    };

    const onDrop = (event: DragEvent) => {
      if (targetHandlesDrop(event.target)) return;
      const text = event.dataTransfer?.getData("text/plain")?.trim();
      if (!text) return;
      event.preventDefault();

      void post<{ note: Note }>("/notes", {
        content: text,
        parent_type: "standalone",
        parent_id: null,
      })
        .then(() => {
          toast({
            title: "Saved to notes",
            description:
              text.length > 80 ? `${text.slice(0, 80)}…` : text,
          });
        })
        .catch(() => {
          toast({
            title: "Couldn't save that",
            description: "The dropped text was not filed. Try again.",
            variant: "destructive",
          });
        });
    };

    window.addEventListener("dragover", onDragOver);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("drop", onDrop);
    };
  }, []);

  // Files dropped from the Finder — Tauri delivers these as paths, not DOM events.
  useEffect(() => {
    if (!isDesktopApp) return;

    const unlisten = getCurrentWebview().onDragDropEvent(async (event) => {
      if (event.payload.type !== "drop") return;
      const paths = event.payload.paths.filter((path) => TEXT_FILE.test(path));
      if (!paths.length || busyRef.current) return;
      busyRef.current = true;

      try {
        const typeId = await importDocTypeId();
        if (typeId == null) throw new Error("No document types available");

        let lastId: number | null = null;
        let imported = 0;
        for (const path of paths.slice(0, 10)) {
          const content = await readTextFile(path);
          const created = await post<{ id: number }>("/documents", {
            title: fileStem(path) || "Imported document",
            content,
            word_count: countWords(content),
            doc_type_id: typeId,
          });
          lastId = created.id;
          imported += 1;
        }

        toast({
          title:
            imported === 1
              ? "Imported into your library"
              : `Imported ${imported} documents`,
          description:
            imported === 1 ? fileStem(paths[0]) : "They are in your library.",
        });
        if (imported === 1 && lastId != null) {
          navigate(`/documents/${lastId}/edit`);
        }
      } catch (error) {
        console.error("Drop import failed:", error);
        toast({
          title: "Import failed",
          description: "The dropped file could not be imported.",
          variant: "destructive",
        });
      } finally {
        busyRef.current = false;
      }
    });

    return () => {
      void unlisten.then((stop) => stop()).catch(() => undefined);
    };
  }, [navigate]);

  return null;
}
